<script>
    import {fade} from 'svelte/transition';

    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import StorageService from '@js/modules/storage';

    import SearchManager from '@js/managers/searchManager';
    import {searchStore} from '@js/stores/searchStore';

    import Icon from '@js/components/themes/Icon.svelte';
    import searchIcon from '@js/components/icons/searchIcon';

    import Autocompletion from '@js/components/search/Autocompletion.svelte';
    import SearchRideFilters from '@js/components/search/filter/SearchRideFilters.svelte';
    import SearchResultList from '@js/components/search/result/SearchResultList.svelte';
    import SearchMap from '@js/components/maps/SearchMap.svelte';
    import SearchQuickStart from '@js/components/search/SearchQuickStart.svelte';
    import SearchSelectedRide from '@js/components/search/SearchSelectedRide.svelte';
    import SearchRecommendation from '@js/components/search/SearchRecommendation.svelte';

    const RECENT_SEARCH_STORAGE_NAME = 'search_page_recent_search';
    const RECENT_SEARCH_LIMIT = 6;

    let searchValue = $state({value: ''});

    let recentSearches = $state(StorageService.getItem(RECENT_SEARCH_STORAGE_NAME) || []);

    const {
        allFilters
    } = $props();

    Utils.onPageReady(() => import(/* webpackChunkName: "maps-search" */ '@js/modules/maps/searchMap'), 20);

    SearchManager.initialize((searchParams) => {
        searchValue = {
            value: searchParams.queryDisplay || searchParams.query,
            query: searchParams.query,
            center: searchParams.location?.center || null,
            bbox: searchParams.location?.bbox || null,
            rideId: searchParams.rideId || null,
            nearMe: searchParams.nearMe || false
        };
    });

    // Typed searches only: "around me" and map areas depend on where the user is
    const _rememberSearch = (search) => {
        if (!search.queryDisplay || search.query === '*') {
            return;
        }

        recentSearches = [search, ...recentSearches.filter((recent) => recent.queryDisplay !== search.queryDisplay)]
            .slice(0, RECENT_SEARCH_LIMIT);

        StorageService.setItem(RECENT_SEARCH_STORAGE_NAME, recentSearches);
    };

    const _performSearch = ({replaceHistory = false} = {}) => {
        const query = searchValue.query || searchValue.value;

        if (!query) {
            SearchManager.clearSearch();

            return;
        }

        const location = searchValue.bbox || searchValue.center ? {bbox: searchValue.bbox, center: searchValue.center} : null;
        const rideId = searchValue.type === 'ride' ? searchValue.id : searchValue.rideId;

        SearchManager.fetchSearch({
            type: searchValue.type,
            query,
            queryDisplay: searchValue.value,
            location,
            rideId,
            nearMe: searchValue.nearMe,
            filters: SearchManager.serializeFilters()
        }, {replaceOnly: replaceHistory});

        _rememberSearch({query, queryDisplay: searchValue.value, location, rideId});
    };

    const _onFilterChange = () => {
        _performSearch({replaceHistory: true});
    };

    const _onOrderChange = (order) => {
        searchStore.order = order;

        _performSearch({replaceHistory: true});
    };

    const _clearFilters = () => {
        searchStore.selectedFilters = {};

        _performSearch({replaceHistory: true});
    };

    const _handleAutocompletionSubmit = () => {
        // A route picked in the suggestions is shown in the results and selected on the map
        searchStore.selectedFilters = {};

        _performSearch();
    };

    const _handleQuickSearch = (search) => {
        searchValue = {
            value: search.queryDisplay,
            query: search.query,
            center: search.location?.center || null,
            bbox: search.location?.bbox || null,
            rideId: search.rideId || null,
            nearMe: search.nearMe || false
        };

        searchStore.selectedFilters = Object.fromEntries(Object.entries(search.filters || {})
            .map(([filterName, filterValue]) => [filterName, Array.isArray(filterValue) ? new Set(filterValue) : filterValue]));

        _performSearch();
    };

    // Search again in the area shown by the map (scaled around its centre), keeping the filters
    const _searchArea = (scale = 1) => {
        const viewport = SearchManager.currentViewport();
        if (!viewport) {
            return;
        }

        const [west, south, east, north] = viewport.bbox;
        const halfWidth = (east - west) * scale / 2;
        const halfHeight = (north - south) * scale / 2;
        const [centerLng, centerLat] = [(west + east) / 2, (south + north) / 2];
        const bbox = [
            Math.max(-180, centerLng - halfWidth),
            Math.max(-85, centerLat - halfHeight),
            Math.min(180, centerLng + halfWidth),
            Math.min(85, centerLat + halfHeight)
        ];

        searchValue = {value: I18n.t('js.search.index.map_area'), query: '*', bbox};

        _performSearch();
    };

    const hasQuery = $derived(Utils.isPresent($searchStore.query));
</script>

<div class="px-3 sm:px-6 lg:px-8 pb-12">
    <!-- Mobile: header, map, list. Desktop: header and list on the left, the map sticky on the right from the top -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:grid-rows-[auto_1fr] lg:gap-x-6">
        <header class="pt-6 lg:col-start-1 lg:row-start-1">
            <h1 class="title-h1 m-0 text-left">
                {I18n.t('js.search.index.title')}
            </h1>

            <p class="mt-1 mb-4 text-base text-gray-600">
                {I18n.t('js.search.index.subtitle')}
            </p>

            <Autocompletion id="global-search"
                            bind:searchValue={searchValue}
                            placeholder={I18n.t('js.search.index.placeholder')}
                            useFirstQueryOnEnter={false}
                            onSubmit={_handleAutocompletionSubmit}/>

            <div class="mt-4">
                {#if hasQuery}
                    <SearchRideFilters allFilters={allFilters}
                                       onFilterUpdate={_onFilterChange}
                                       onOrderUpdate={_onOrderChange}/>
                {:else}
                    <SearchQuickStart rideTypes={allFilters.rideTypes}
                                      recentSearches={recentSearches}
                                      onSearch={_handleQuickSearch}/>
                {/if}
            </div>
        </header>

        <div id="map-global-search"
             class="h-[61vh] min-h-[378px] lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-start lg:sticky lg:top-20 lg:mt-6 lg:h-[calc(100vh-7rem)]">
            <SearchMap mapId="advanced-search"
                       class="h-full">
                {#if $searchStore.canSearchArea}
                    <div class="absolute top-3 inset-x-0 flex justify-center pointer-events-none z-10"
                         transition:fade={{duration: 150}}>
                        <button type="button"
                                class="pointer-events-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-200 rounded-full shadow-md hover:bg-gray-50 cursor-pointer"
                                onclick={() => _searchArea()}>
                            <Icon class="w-4 h-4 text-primary-700"
                                  icon={searchIcon}/>
                            {I18n.t('js.search.index.search_area')}
                        </button>
                    </div>
                {/if}

                <SearchSelectedRide/>
            </SearchMap>
        </div>

        <div class="min-w-0 lg:col-start-1 lg:row-start-2">
            <SearchResultList onClearFilters={_clearFilters}
                              onSearchArea={() => _searchArea(3)}/>
        </div>
    </div>

    <SearchRecommendation/>
</div>
